import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check browser support
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);

    // Detect Safari
    const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsSafari(safari);

    // Detect standalone mode (PWA installed)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    // Get current permission
    if (supported) {
      setPermission(Notification.permission);
      checkSubscriptionStatus();
    }
  }, [user]);

  const checkSubscriptionStatus = async () => {
    if (!user) {
      console.log('[Push] No user - setting isSubscribed to false');
      setIsSubscribed(false);
      return;
    }

    try {
      console.log('[Push] Checking subscription status for user:', user.id);
      
      // Check browser subscription
      const registration = await navigator.serviceWorker.ready;
      const browserSubscription = await registration.pushManager.getSubscription();
      
      console.log('[Push] Browser subscription exists:', !!browserSubscription);
      
      if (!browserSubscription) {
        console.log('[Push] No browser subscription - marking as not subscribed');
        setIsSubscribed(false);
        return;
      }

      console.log('[Push] Browser endpoint:', browserSubscription.endpoint?.substring(0, 60) + '...');
      
      // Verify subscription exists in database and is active
      const { data: dbSubscription, error } = await supabase
        .from('push_subscriptions')
        .select('id, is_active, endpoint')
        .eq('user_id', user.id)
        .eq('endpoint', browserSubscription.endpoint)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) {
        console.error('[Push] Database check error:', error);
        setIsSubscribed(false);
        return;
      }

      if (!dbSubscription) {
        console.log('[Push] No matching active DB subscription found');
        console.log('[Push] Browser has subscription but DB does not - may need to re-subscribe');
        setIsSubscribed(false);
        return;
      }
      
      console.log('[Push] ✅ Subscription valid in both browser and database');
      console.log('[Push] DB subscription ID:', dbSubscription.id);
      setIsSubscribed(true);
    } catch (error) {
      console.error('[Push] Error checking subscription status:', error);
      setIsSubscribed(false);
    }
  };

  const getVapidKey = async (): Promise<Uint8Array> => {
    const { data, error } = await supabase.functions.invoke('get-vapid-key');
    
    if (error) {
      throw new Error('Failed to fetch VAPID key');
    }

    const publicKey = data.publicKey;
    
    // Convert base64 to Uint8Array
    const padding = '='.repeat((4 - (publicKey.length % 4)) % 4);
    const base64 = (publicKey + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  };

  const subscribe = async () => {
    console.log('[Push] Subscribe called - user:', user?.id);
    
    if (!user) {
      console.error('[Push] Subscribe failed - no user');
      throw new Error('User not authenticated');
    }

    setIsLoading(true);

    try {
      console.log('[Push] Requesting notification permission...');
      // Request permission
      const permissionResult = await Notification.requestPermission();
      console.log('[Push] Permission result:', permissionResult);
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        console.warn('[Push] Permission denied by user');
        throw new Error('Notification permission denied');
      }

      console.log('[Push] Getting service worker registration...');
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready, scope:', registration.scope);

      console.log('[Push] Fetching VAPID key...');
      // Get VAPID key
      const applicationServerKey = await getVapidKey();
      console.log('[Push] VAPID key received, length:', applicationServerKey.length);

      console.log('[Push] Subscribing to push manager...');
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });
      console.log('[Push] ✅ Push manager subscription successful');
      console.log('[Push] Endpoint:', subscription.endpoint);

      // Extract keys from subscription
      const subscriptionJSON = subscription.toJSON();
      const p256dhKey = subscriptionJSON.keys?.p256dh || null;
      const authKey = subscriptionJSON.keys?.auth || null;

      console.log('[Push] Keys extracted - p256dh:', !!p256dhKey, 'auth:', !!authKey);

      // Get browser info
      const browserInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
        isStandalone: window.matchMedia('(display-mode: standalone)').matches,
      };

      console.log('[Push] Browser info:', JSON.stringify(browserInfo));

      // First, deactivate any existing subscriptions for this endpoint
      console.log('[Push] Deactivating any existing subscriptions for this endpoint...');
      await supabase
        .from('push_subscriptions')
        .update({ is_active: false })
        .eq('endpoint', subscription.endpoint);

      // Save subscription to database
      console.log('[Push] Saving NEW subscription to database for user:', user.id);
      const { data: insertData, error } = await supabase.from('push_subscriptions').insert({
        user_id: user.id,
        subscription_data: subscriptionJSON,
        endpoint: subscription.endpoint,
        p256dh_key: p256dhKey,
        auth_key: authKey,
        browser_info: browserInfo,
        is_active: true,
      } as any).select().single();
      
      if (error) {
        console.error('[Push] Database insert error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        
        // If unique constraint violation, update existing
        if (error.code === '23505') {
          console.log('[Push] Unique constraint - updating existing subscription');
          const { error: updateError } = await supabase
            .from('push_subscriptions')
            .update({ 
              is_active: true, 
              updated_at: new Date().toISOString(),
              p256dh_key: p256dhKey,
              auth_key: authKey,
              browser_info: browserInfo,
            })
            .eq('user_id', user.id)
            .eq('endpoint', subscription.endpoint);
          
          if (updateError) {
            console.error('[Push] Update failed:', updateError);
            throw updateError;
          }
          console.log('[Push] ✅ Existing subscription updated successfully');
        } else {
          throw error;
        }
      } else {
        console.log('[Push] ✅ New subscription inserted, ID:', insertData?.id);
      }

      console.log('[Push] ✅ Subscription process completed successfully');
      setIsSubscribed(true);
    } catch (error: any) {
      console.error('[Push] Subscribe failed with error:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    if (!user) return;

    setIsLoading(true);

    try {
      console.log('[Push] Unsubscribing...');
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        console.log('[Push] Browser subscription removed');

        // Mark subscription as inactive in database
        const { error } = await supabase
          .from('push_subscriptions')
          .update({ is_active: false })
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint);

        if (error) {
          console.error('[Push] Failed to deactivate in DB:', error);
        } else {
          console.log('[Push] ✅ Database subscription deactivated');
        }

        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('[Push] Error unsubscribing:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const setBadge = async (count: number) => {
    if ('setAppBadge' in navigator) {
      try {
        await (navigator as any).setAppBadge(count);
      } catch (error) {
        console.error('Error setting badge:', error);
      }
    }
  };

  const clearBadge = async () => {
    if ('clearAppBadge' in navigator) {
      try {
        await (navigator as any).clearAppBadge();
      } catch (error) {
        console.error('Error clearing badge:', error);
      }
    }
  };

  return {
    permission,
    isSupported,
    isSafari,
    isStandalone,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    setBadge,
    clearBadge,
  };
};
